import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Eraser, Loader2, PenLine, Trash2, Upload } from 'lucide-react';
import { adminApi } from '../services/api';
import {
  annoncerErreur, annoncerSucces, confirmer, EtatErreur, SqueletteCarte, validerFichier,
} from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   MA SIGNATURE

   Enregistrée une fois, puis réutilisée. Elle ne quitte jamais le
   stockage privé du serveur : elle n'apparaît ailleurs qu'incrustée dans
   les documents que son titulaire a lui-même choisi de signer.

   Deux gestes, parce que les deux existent dans la vraie vie : dessiner
   à l'écran (souris, doigt, stylet) ou photographier une signature faite
   au stylo. Le serveur détoure la photo — sans cela, elle s'incrusterait
   sur le document comme un rectangle de papier gris.

   Le canvas est dessiné à **deux fois** la taille affichée
   (`devicePixelRatio` mis à part) : un trait enregistré à la taille de
   l'écran arrive crénelé sur un PDF imprimé à 300 points par pouce.
   ═══════════════════════════════════════════════════════════════════════ */

const LARGEUR = 520;
const HAUTEUR = 180;

export default function MaSignature({ onEnregistree }) {
  const toile = useRef(null);
  const contexte = useRef(null);
  const traceEnCours = useRef(false);
  const aTrace = useRef(false);

  const [etat, setEtat] = useState(null);
  const [apercu, setApercu] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // L'objet erreur du chargement : son statut choisit l'écran.
  const [erreurChargement, setErreurChargement] = useState(null);
  const [vide, setVide] = useState(true);

  const charger = useCallback(async () => {
    let resultat;
    try {
      resultat = await adminApi.maSignature();
      setErreurChargement(null);
    } catch (e) {
      setErreurChargement(e);
      return;
    }
    setEtat(resultat);
    if (!resultat.enregistree) { setApercu(''); return; }
    try {
      // En blob : une balise `<img src>` n'envoie pas le jeton, et le
      // fichier est privé — il est servi par une vue qui l'exige.
      setApercu(URL.createObjectURL(await adminApi.imageMaSignature()));
    } catch (e) {
      annoncerErreur("L'aperçu de votre signature n'a pas pu être affiché.");
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Sans révocation, chaque rechargement retient une image de plus.
  useEffect(() => () => { if (apercu) URL.revokeObjectURL(apercu); }, [apercu]);

  /* Le canvas se prépare une fois. Le redimensionner ensuite effacerait
     le tracé en cours — d'où la taille fixe et le `max-width` en CSS. */
  useEffect(() => {
    const el = toile.current;
    if (!el) return;
    const echelle = Math.min(2, window.devicePixelRatio || 1) * 2;
    el.width = LARGEUR * echelle;
    el.height = HAUTEUR * echelle;
    const ctx = el.getContext('2d');
    ctx.scale(echelle, echelle);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#17231D';
    contexte.current = ctx;
  }, [etat]);

  const pointer = (evenement) => {
    const rect = toile.current.getBoundingClientRect();
    const source = evenement.touches?.[0] || evenement;
    return {
      x: (source.clientX - rect.left) * (LARGEUR / rect.width),
      y: (source.clientY - rect.top) * (HAUTEUR / rect.height),
    };
  };

  const commencer = (evenement) => {
    evenement.preventDefault();
    traceEnCours.current = true;
    const { x, y } = pointer(evenement);
    contexte.current.beginPath();
    contexte.current.moveTo(x, y);
    // Un point seul est un tracé valable : une initiale, un point sur un i.
    contexte.current.lineTo(x + 0.1, y + 0.1);
    contexte.current.stroke();
    aTrace.current = true;
    setVide(false);
  };

  const continuer = (evenement) => {
    if (!traceEnCours.current) return;
    evenement.preventDefault();
    const { x, y } = pointer(evenement);
    contexte.current.lineTo(x, y);
    contexte.current.stroke();
  };

  const finir = () => { traceEnCours.current = false; };

  const effacer = () => {
    const el = toile.current;
    contexte.current.clearRect(0, 0, el.width, el.height);
    aTrace.current = false;
    setVide(true);
  };

  const enregistrerLeTrace = async () => {
    if (!aTrace.current) return;
    setEnvoi(true);
    try {
      const resultat = await adminApi.enregistrerMaSignature({
        trace: toile.current.toDataURL('image/png'),
      });
      annoncerSucces('Signature enregistrée.');
      effacer();
      await charger();
      onEnregistree?.(resultat);
    } catch (e) {
      annoncerErreur(e.message || "La signature n'a pas pu être enregistrée.");
    } finally {
      setEnvoi(false);
    }
  };

  const envoyerUnePhoto = async (evenement) => {
    const fichier = evenement.target.files?.[0];
    if (!fichier) return;
    const probleme = validerFichier(fichier, { extensions: ['.png', '.jpg', '.jpeg', '.webp'] });
    if (probleme) { annoncerErreur(probleme); evenement.target.value = ''; return; }
    setEnvoi(true);
    try {
      const corps = new FormData();
      corps.append('image', fichier);
      const resultat = await adminApi.enregistrerMaSignature(corps);
      annoncerSucces('Signature enregistrée — le fond du papier a été retiré.');
      await charger();
      onEnregistree?.(resultat);
    } catch (e) {
      annoncerErreur(e.message || "La photo n'a pas pu être enregistrée.");
    } finally {
      setEnvoi(false);
      evenement.target.value = '';
    }
  };

  const supprimer = async () => {
    if (!await confirmer(
      "Supprimer votre signature enregistrée ?\n\n"
      + "Les documents que vous avez déjà signés ne changent pas : ils en "
      + "portent une copie.")) return;
    setEnvoi(true);
    try {
      await adminApi.supprimerMaSignature();
      setEtat({ enregistree: false });
      setApercu('');
      annoncerSucces('Signature supprimée.');
      onEnregistree?.({ enregistree: false });
    } catch (e) {
      annoncerErreur(e.message || "La signature n'a pas pu être supprimée.");
    } finally {
      setEnvoi(false);
    }
  };

  if (etat === null) {
    return erreurChargement
      ? <EtatErreur erreur={erreurChargement} onReessayer={charger} />
      : <SqueletteCarte />;
  }

  return (
    <div className="rounded-carte bg-[var(--bg-card)] shadow-carte p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="titre-carte">Ma signature</h3>
          <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
            {etat.enregistree
              ? `Enregistrée le ${new Date(etat.enregistree_le).toLocaleDateString('fr-FR')}.`
              : "Pas encore enregistrée : vous ne pouvez pas signer de document."}
          </p>
        </div>
        {etat.enregistree && (
          <button type="button" onClick={supprimer} disabled={envoi}
                  className="act act-tertiaire" title="Supprimer">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {etat.enregistree && apercu && (
        <div className="rounded-pastille bg-[var(--bg-main)] p-4 flex justify-center">
          {/* Servie par une vue qui vérifie les droits : il n'existe pas
              d'adresse permettant de demander celle d'un autre. */}
          <img src={apercu} alt="Votre signature" className="max-h-24" />
        </div>
      )}

      <div>
        <span className="block surtitre text-[var(--text-muted)] mb-1.5">
          {etat.enregistree ? 'La remplacer — dessinez ici' : 'Dessinez votre signature'}
        </span>
        <canvas
          ref={toile}
          onMouseDown={commencer} onMouseMove={continuer}
          onMouseUp={finir} onMouseLeave={finir}
          onTouchStart={commencer} onTouchMove={continuer} onTouchEnd={finir}
          style={{ width: '100%', maxWidth: LARGEUR, height: 'auto',
                   aspectRatio: `${LARGEUR} / ${HAUTEUR}`, touchAction: 'none' }}
          className="block rounded-pastille bg-white border border-[var(--border)]
                     cursor-crosshair"
          aria-label="Zone de dessin de la signature"
        />
        <p className="text-[11.5px] text-[var(--text-muted)] mt-1.5">
          À la souris, au doigt ou au stylet. Le tracé est recadré et
          normalisé : inutile de viser le centre.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={effacer} disabled={vide || envoi}
                className="act act-tertiaire">
          <Eraser className="w-4 h-4" />
          Effacer
        </button>
        <button type="button" onClick={enregistrerLeTrace} disabled={vide || envoi}
                aria-busy={envoi || undefined} className="act">
          {envoi ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Check className="w-4 h-4" />}
          Enregistrer ce tracé
        </button>
        <label className={`act act-contour ${envoi ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
               aria-disabled={envoi || undefined}>
          <Upload className="w-4 h-4" />
          Envoyer une photo
          <input type="file" accept="image/png,image/jpeg,image/webp"
                 className="hidden" disabled={envoi} onChange={envoyerUnePhoto} />
        </label>
      </div>

      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        <PenLine className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />
        Photographiez votre signature sur une feuille blanche, bien
        éclairée : le fond du papier est retiré automatiquement. Votre
        signature reste sur le serveur, jamais publiée — elle n’apparaît
        qu’incrustée dans les documents que vous signez vous-même.
      </p>
    </div>
  );
}
